import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { Upload, Trash2, FileText, CheckCircle, AlertCircle, Clock, Search, Database } from 'lucide-react';

function Notification({ message, type, onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const colors = type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800';
    return (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border-l-4 shadow-lg ${colors} flex items-center gap-2`}>
            {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message}
        </div>
    );
}

export default function DocumentsPage() {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [notif, setNotif] = useState(null);
    const fileInputRef = useRef(null);

    const fetchDocuments = () => {
        api.get('/documents').then(({ data }) => setDocuments(data)).catch(() => { }).finally(() => setLoading(false));
    };

    useEffect(() => { fetchDocuments(); }, []);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);
        let success = 0;
        let errors = 0;

        for (const file of files) {
            const fd = new FormData();
            fd.append('file', file);
            try {
                await api.post('/documents/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                success++;
            } catch (err) {
                errors++;
            }
        }

        setUploading(false);
        fileInputRef.current.value = '';

        if (success > 0) {
            setNotif({ type: 'success', message: `${success} document(s) uploadé(s)` });
        }
        if (errors > 0) {
            setNotif({ type: 'error', message: `${errors} échec(s) d'upload` });
        }

        fetchDocuments();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce document ? Les vecteurs associés seront aussi supprimés.')) return;
        try {
            await api.delete(`/documents/${id}`);
            setNotif({ type: 'success', message: 'Document supprimé' });
            fetchDocuments();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur de suppression' });
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '—';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    return (
        <div>
            {notif && <Notification {...notif} onClose={() => setNotif(null)} />}

            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Documents RAG</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Uploadez des documents pour enrichir les connaissances de l'IA
                    </p>
                </div>
                <div>
                    <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,.md,.html,.docx"
                        className="hidden" onChange={handleUpload} />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className="flex items-center gap-2 bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
                        <Upload className="w-4 h-4" />
                        {uploading ? 'Upload en cours...' : 'Uploader'}
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#5764F4]" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
                        <p className="text-xs text-gray-500">Documents</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                        <Database className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">{documents.filter(d => d.indexed).length}</p>
                        <p className="text-xs text-gray-500">Indexés</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                        <Search className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">
                            {documents.reduce((sum, d) => sum + (d.chunk_count || 0), 0)}
                        </p>
                        <p className="text-xs text-gray-500">Chunks totaux</p>
                    </div>
                </div>
            </div>

            {/* Document list */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {documents.length === 0 ? (
                    <div className="text-center py-16 px-4">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Aucun document</p>
                        <p className="text-gray-400 text-sm mt-1">
                            Uploadez des fichiers PDF, TXT, Markdown ou HTML pour enrichir votre IA
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {documents.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${doc.indexed ? 'bg-green-50' : 'bg-gray-50'}`}>
                                        <FileText className={`w-5 h-5 ${doc.indexed ? 'text-green-600' : 'text-gray-400'}`} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                                            <span>{formatFileSize(doc.file_size)}</span>
                                            <span>•</span>
                                            <span>{formatDate(doc.created_at)}</span>
                                            {doc.chunk_count > 0 && (
                                                <>
                                                    <span>•</span>
                                                    <span>{doc.chunk_count} chunks</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 ml-4">
                                    {doc.indexed ? (
                                        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Indexé
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                                            <Clock className="w-3 h-3" /> En attente
                                        </span>
                                    )}
                                    <button onClick={() => handleDelete(doc.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
